import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Button } from '@marriott/mi-ui-library';
import { AIcon } from '@marriott/mi-web-ui-kit';
import { DEFAULT_HEART_ANIMATION_DURATION_MS } from '../../../modules/utils/constants/constants';
import { StyledWishlistHeartButton } from './WishlistHeartButton.styles';
import { WishlistHeartButtonProps } from './WishlisHeartButton.types';

const HEART_POP_KEYFRAMES: Keyframe[] = [
  { transform: 'scale(1)' },
  { transform: 'scale(1.55)', offset: 0.35 },
  { transform: 'scale(1.10)', offset: 0.6 },
  { transform: 'scale(1)' },
];

const HEART_POP_OPTIONS: KeyframeAnimationOptions = {
  duration: DEFAULT_HEART_ANIMATION_DURATION_MS,
  easing: 'ease-in-out',
  fill: 'forwards',
};

export const WishlistHeartButton = ({
  isFavorited,
  onAnimationComplete,
  onToggle,
  className,
  dataTestId,
  disabled = false,
  iconClass = 'icon-s',
  activeIconClass = 'icon-heart-fill',
  inactiveIconClass = 'icon-heart-outline',
  animateOnActivateOnly = true,
}: WishlistHeartButtonProps) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const iconRef = useRef<HTMLSpanElement>(null);
  const pendingNextValueRef = useRef<boolean | null>(null);
  const animationTimerRef = useRef<number | null>(null);
  const activeAnimationRef = useRef<Animation | null>(null);
  const onAnimationCompleteRef = useRef(onAnimationComplete);
  const onToggleRef = useRef(onToggle);
  onAnimationCompleteRef.current = onAnimationComplete;
  onToggleRef.current = onToggle;

  const notifyToggle = (nextFavorited: boolean) => {
    onToggleRef.current?.(nextFavorited);
    onAnimationCompleteRef.current?.(nextFavorited);
  };

  useEffect(() => {
    return () => {
      activeAnimationRef.current?.cancel();
      if (animationTimerRef.current) {
        window.clearTimeout(animationTimerRef.current);
      }
    };
  }, []);

  const completeAnimation = () => {
    const nextFavorited = pendingNextValueRef.current ?? isFavorited;

    setIsAnimating(false);
    pendingNextValueRef.current = null;
    activeAnimationRef.current = null;

    if (animationTimerRef.current) {
      window.clearTimeout(animationTimerRef.current);
      animationTimerRef.current = null;
    }

    if (iconRef.current) {
      iconRef.current.style.transform = '';
    }

    notifyToggle(nextFavorited);
  };
  useLayoutEffect(() => {
    if (!isAnimating || !iconRef.current) {
      return;
    }
    const iconElement = iconRef.current;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion || typeof iconElement.animate !== 'function') {
      completeAnimation();
      return;
    }
    activeAnimationRef.current?.cancel();
    iconElement.style.transform = 'scale(1)';
    const animation = iconElement.animate(HEART_POP_KEYFRAMES, HEART_POP_OPTIONS);
    activeAnimationRef.current = animation;
    animation.onfinish = () => {
      completeAnimation();
    };

    animationTimerRef.current = window.setTimeout(() => {
      completeAnimation();
    }, DEFAULT_HEART_ANIMATION_DURATION_MS + 50);

    return () => {
      animation.cancel();
      if (animationTimerRef.current) {
        window.clearTimeout(animationTimerRef.current);
        animationTimerRef.current = null;
      }
    };
  }, [isAnimating, completeAnimation, isFavorited]);

  const handleClick = () => {
    if (disabled || isAnimating) {
      return;
    }
    const nextFavorited = !isFavorited;
    pendingNextValueRef.current = nextFavorited;

    if (animateOnActivateOnly && !nextFavorited) {
      pendingNextValueRef.current = null;
      notifyToggle(nextFavorited);
      return;
    }

    setIsAnimating(true);
  };

  return (
    <StyledWishlistHeartButton>
      <Button
        className={clsx(
          'wmlp__heart-btn',
          'm-heart-pop-button',
          {
            'wmlp__heart-btn--favorited': isFavorited,
            'm-heart-pop-button--active': isFavorited,
          },
          className
        )}
        data-testid={dataTestId}
        aria-label={isFavorited ? activeIconClass : inactiveIconClass}
        aria-pressed={isFavorited}
        isDisabled={disabled}
        callback={handleClick}
      >
        <span ref={iconRef} className="m-heart-pop-button__icon">
          <AIcon icon={isFavorited ? activeIconClass : inactiveIconClass} iconClass={iconClass} />
        </span>
      </Button>
    </StyledWishlistHeartButton>
  );
};
